import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";

interface InternalEmailsState {
  loading: boolean;
  success: boolean;
  error: string | null;
  finalEmails: string[];
  missingCaseIds: string[];
}

const initialState: InternalEmailsState = {
  loading: false,
  success: false,
  error: null,
  finalEmails: [],
  missingCaseIds: [],
};

export const sendCaseOkEmails = createAsyncThunk<
  { finalEmails: string[] },
  { case_id: string[]; sendBy: string },
  { rejectValue: { message: string; missing_case_ids?: string[] } }
>(
  "internalEmails/sendCaseOkEmails",
  async ({ case_id, sendBy }, thunkAPI) => {
    try {
      const response = await api.post(
        `/email/caseOk`,
        { case_id, sendBy }
      );
      return response.data;
    } catch (err: any) {
      if (err.response && err.response.data) {
        return thunkAPI.rejectWithValue({
          message: err.response.data.message,
          missing_case_ids: err.response.data.missing_case_ids || [],
        });
      }
      return thunkAPI.rejectWithValue({ message: "Unexpected error" });
    }
  }
);

export const sendDesignReadyEmails = createAsyncThunk<
  { finalEmails: string[] },
  { cases: { case_id: string; note: string }[]; sendBy: string },
  { rejectValue: { message: string; missing_case_ids?: string[] } }
>(
  "internalEmails/sendDesignReadyEmails",
  async ({ cases, sendBy }, thunkAPI) => {
    try {
      const response = await api.post(
        `/email/designReady`,
        { cases, sendBy } // send notes along with case_id
      );
      return response.data;
    } catch (err: any) {
      if (err.response && err.response.data) {
        return thunkAPI.rejectWithValue({
          message: err.response.data.message,
          missing_case_ids: err.response.data.missing_case_ids || [],
        });
      }
      return thunkAPI.rejectWithValue({ message: "Unexpected error" });
    }
  }
);

export const sendQueryEmails = createAsyncThunk<
  { success: boolean; message: string },
  { name: string; email: string; note?: string },
  { rejectValue: { message: string } }
>("internalEmails/sendQueryEmails", async ({ name, email, note }, thunkAPI) => {
  try {
    const response = await api.post(
      `/email/queryEmail`,
      { name, email, note }
    );
    return response.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue({
      message: err?.response?.data?.message || "Unexpected error",
    });
  }
});

const internalEmailsSlice = createSlice({
  name: "internalEmails",
  initialState,
  reducers: {
    resetStatus(state) {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.finalEmails = [];
      state.missingCaseIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendCaseOkEmails.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
        state.missingCaseIds = [];
      })
      .addCase(sendCaseOkEmails.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.finalEmails = action.payload.finalEmails;
      })
      .addCase(sendCaseOkEmails.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to send emails";
        state.missingCaseIds = action.payload?.missing_case_ids || [];
      })

      .addCase(sendDesignReadyEmails.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
        state.missingCaseIds = [];
      })
      .addCase(sendDesignReadyEmails.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.finalEmails = action.payload.finalEmails;
      })
      .addCase(sendDesignReadyEmails.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload?.message || "Failed to send emails";
        state.missingCaseIds = action.payload?.missing_case_ids || [];
      })

      .addCase(sendQueryEmails.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(sendQueryEmails.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.success;
      })
      .addCase(sendQueryEmails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed";
      });
  },
});

export const { resetStatus } = internalEmailsSlice.actions;
export default internalEmailsSlice.reducer;
